import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { 
  Search, MapPin, Star, Wifi, Car, Coffee, Waves, Dumbbell, Loader2,
  Users, ChevronRight, ChevronLeft, Utensils,
  Shield, Clock, CreditCard, Building2, Map, Check, Sparkles, ChevronDown, ChevronUp, TrendingDown
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/App';
import PortalHeader from './PortalHeader';
import PortalDateRangePicker from './PortalDateRangePicker';

const FILTER_OPTIONS = {
  essentials: [
    { id: 'pay_at_hotel', label: 'Pay @ Hotel Available', icon: CreditCard },
    { id: 'wifi', label: 'Wi-Fi', icon: Wifi },
    { id: 'breakfast', label: 'Breakfast Included', icon: Utensils },
    { id: 'free_cancel', label: 'Free Cancellation', icon: Shield },
    { id: 'parking', label: 'Free Parking', icon: Car },
  ],
  suggestions: [
    { id: 'last_minute', label: 'Last Minute Deals', icon: Clock },
    { id: 'best_rated', label: 'Best Rated', icon: Star },
    { id: 'pool', label: 'Swimming Pool', icon: Waves },
    { id: 'gym', label: 'Fitness Center', icon: Dumbbell },
  ]
};

const SORT_OPTIONS = [
  { id: 'popularity', label: 'Popularity' },
  { id: 'price_low', label: 'Price (Low to High)' },
  { id: 'price_high', label: 'Price (High to Low)' },
  { id: 'rating', label: 'User Rating (Highest)' },
  { id: 'best_value', label: 'Lowest Price & Best Rated' },
];

const BookingPortalSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize portal settings from cache to prevent flash
  const [portalSettings, setPortalSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('portal_settings_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.primary_color && parsed.secondary_color) {
          return { show_competitor_prices: false, ...parsed };
        }
      }
    } catch (e) {}
    return {
      portal_name: 'Book Your Stay',
      primary_color: '#d4a373',
      secondary_color: '#2d3748',
      show_competitor_prices: false
    };
  });
  
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    guests: searchParams.get('guests') || '2',
    rooms: searchParams.get('rooms') || '1',
    sortBy: 'popularity'
  });
  
  const [dateRange, setDateRange] = useState(() => {
    const checkIn = searchParams.get('check_in');
    const checkOut = searchParams.get('check_out');
    return {
      from: checkIn ? new Date(checkIn + 'T00:00:00') : undefined,
      to: checkOut ? new Date(checkOut + 'T00:00:00') : undefined,
    };
  });
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [selectedFilters, setSelectedFilters] = useState(new Set());
  const [portalPrices, setPortalPrices] = useState({});
  const [roomPrices, setRoomPrices] = useState({}); // Store room-level AI prices with competitors
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [loadingRoomPrices, setLoadingRoomPrices] = useState({}); // Track loading state per room
  const [expandedCompetitors, setExpandedCompetitors] = useState({}); // Track which rooms have competitor panel open

  useEffect(() => {
    fetchPortalSettings();
  }, []);

  useEffect(() => {
    searchProperties();
  }, [page, filters.sortBy]);

  const getCheckInStr = () => dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '';
  const getCheckOutStr = () => dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '';

  const fetchPortalSettings = async () => {
    try {
      const response = await api.get('/portal/settings');
      const newSettings = { ...portalSettings, ...response.data };
      setPortalSettings(newSettings);
      // Cache settings
      localStorage.setItem('portal_settings_cache', JSON.stringify(newSettings));
    } catch (error) {
      console.error('Failed to fetch portal settings:', error);
    }
  };

  const searchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.location) params.append('location', filters.location);
      const ci = getCheckInStr();
      const co = getCheckOutStr();
      if (ci) params.append('check_in', ci);
      if (co) params.append('check_out', co);
      params.append('page', page.toString());
      params.append('limit', '10');
      
      const response = await api.get(`/portal/properties?${params}`);
      const propertiesData = response.data.properties || [];
      setProperties(propertiesData);
      setTotal(response.data.total || 0);
      setLoading(false); // Page loads immediately
      
      // Fetch dynamic prices asynchronously after page is loaded
      if (propertiesData.length > 0 && ci && co) {
        fetchDynamicPrices(propertiesData, ci, co);
        fetchAllRoomPrices(propertiesData, ci, co);
      }
    } catch (error) {
      console.error('Failed to search properties:', error);
      setLoading(false);
    }
  };

  // Fetch property-level dynamic prices
  const fetchDynamicPrices = async (props, checkIn, checkOut) => {
    setLoadingPrices(true);
    const prices = {};
    await Promise.all(
      props.map(async (property) => {
        try {
          const basePrice = property.min_price || property.starting_price;
          if (basePrice) {
            const res = await api.post(`/dynamic-pricing/portal-price?hotel_id=${property.id}`, {
              hotel_name: property.name,
              city: property.city || 'India',
              room_type: 'Standard Room',
              check_in: checkIn,
              check_out: checkOut,
              base_price: basePrice
            });
            prices[property.id] = res.data;
          }
        } catch (err) {
          console.error(`Dynamic pricing error for ${property.name}:`, err);
        }
      })
    );
    setPortalPrices(prices);
    setLoadingPrices(false);
  };

  // Fetch AI prices for all room types
  const fetchAllRoomPrices = async (props, checkIn, checkOut) => {
    const allRoomIds = {};
    props.forEach(property => {
      (property.room_types || []).forEach(room => {
        allRoomIds[room.id] = true;
      });
    });
    
    // Set all rooms to loading state
    setLoadingRoomPrices(allRoomIds);
    
    const prices = {};
    
    // Fetch prices for each room type in parallel
    await Promise.all(
      props.flatMap(property => 
        (property.room_types || []).map(async (room) => {
          try {
            const res = await api.post(`/dynamic-pricing/portal-price?hotel_id=${property.id}`, {
              hotel_name: property.name,
              city: property.city || 'India',
              room_type: room.name,
              check_in: checkIn,
              check_out: checkOut,
              base_price: room.base_price || 0
            });
            prices[room.id] = res.data;
            
            // Update loading state for this room
            setLoadingRoomPrices(prev => {
              const updated = { ...prev };
              delete updated[room.id];
              return updated;
            });
            
            // Update room prices incrementally
            setRoomPrices(prev => ({ ...prev, [room.id]: res.data }));
          } catch (err) {
            console.error(`Room pricing error for ${room.name}:`, err);
            setLoadingRoomPrices(prev => {
              const updated = { ...prev };
              delete updated[room.id];
              return updated;
            });
          }
        })
      )
    );
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    const ci = getCheckInStr();
    const co = getCheckOutStr();
    if (ci) params.set('check_in', ci);
    if (co) params.set('check_out', co);
    if (filters.guests) params.set('guests', filters.guests);
    setSearchParams(params);
    setPage(1);
    searchProperties();
  };

  const handlePropertyClick = (property) => {
    const params = new URLSearchParams();
    const ci = getCheckInStr();
    const co = getCheckOutStr();
    if (ci) params.set('check_in', ci);
    if (co) params.set('check_out', co);
    if (filters.guests) params.set('guests', filters.guests);
    const queryString = params.toString();
    navigate(`/property/${property.id}${queryString ? `?${queryString}` : ''}`);
  };

  const handleQuickBook = (property, roomType) => {
    // Quick book navigates to property page with room pre-selected for direct booking
    const params = new URLSearchParams();
    const ci = getCheckInStr();
    const co = getCheckOutStr();
    if (ci) params.set('check_in', ci);
    if (co) params.set('check_out', co);
    if (filters.guests) params.set('guests', filters.guests);
    params.set('quick_book', 'true');
    params.set('room_type_id', roomType.id);
    const queryString = params.toString();
    navigate(`/property/${property.id}?${queryString}`);
  };

  const hasDates = !!(dateRange?.from && dateRange?.to);

  const toggleFilter = (filterId) => {
    setSelectedFilters(prev => {
      const next = new Set(prev);
      next.has(filterId) ? next.delete(filterId) : next.add(filterId);
      return next;
    });
  };

  const calculateNights = () => {
    if (dateRange?.from && dateRange?.to) {
      return Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24));
    }
    return 1;
  };

  const getPropertyRating = (property) => {
    const rating = property.rating || property.average_rating || 4.2;
    if (rating >= 4.5) return { score: rating, label: 'Excellent', color: 'bg-green-700' };
    if (rating >= 4.0) return { score: rating, label: 'Very Good', color: 'bg-green-600' };
    if (rating >= 3.5) return { score: rating, label: 'Good', color: 'bg-yellow-600' };
    return { score: rating, label: 'Average', color: 'bg-gray-500' };
  };

  return (
    <div className="min-h-screen bg-[#f2f2f2]">
      <PortalHeader portalSettings={portalSettings} />
      
      {/* Top Search Bar */}
      <div className="sticky top-0 z-40" style={{ backgroundColor: '#041422' }}>
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="flex items-stretch h-[70px]">
            {/* Location */}
            <div className="flex-1 flex flex-col justify-center px-4 border-r border-white/10 min-w-0">
              <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider">City, Area or Property</span>
              <Input
                placeholder="Search location..."
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="border-0 bg-transparent text-white p-0 h-7 text-sm font-semibold placeholder:text-white/40 focus-visible:ring-0 shadow-none"
                data-testid="search-location"
              />
            </div>
            
            {/* Date Range Picker */}
            <PortalDateRangePicker 
              dateRange={dateRange} 
              onDateRangeChange={setDateRange} 
              variant="dark" 
            />
            
            {/* Rooms & Guests */}
            <div className="flex flex-col justify-center px-4 border-r border-white/10 min-w-[150px]">
              <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider">Rooms & Guests</span>
              <span className="text-sm font-semibold text-white">{filters.rooms} Room, {filters.guests} Adults</span>
            </div>
            
            {/* Search Button */}
            <div className="flex items-center pl-4">
              <Button 
                onClick={handleSearch}
                className="h-[42px] px-10 text-white font-bold text-base rounded-full"
                style={{ background: 'linear-gradient(93deg, #53b2fe, #065af3)' }}
                data-testid="search-btn"
              >
                SEARCH
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 py-5">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
          <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate('/')}>Home</span>
          <ChevronRight className="h-3 w-3" />
          <span>Hotels</span>
          {filters.location && (
            <>
              <ChevronRight className="h-3 w-3" />
              <span className="text-gray-700">{filters.location}</span>
            </>
          )}
        </div>

        <div className="flex gap-5">
          {/* Left Sidebar */}
          <div className="w-[240px] flex-shrink-0 hidden lg:block">
            {/* Map */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm mb-4">
              <div className="h-[120px] bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center">
                <Map className="h-10 w-10 text-blue-400/60" />
              </div>
              <div className="p-2.5 text-center">
                <button className="text-blue-600 text-xs font-bold flex items-center justify-center gap-1 w-full">
                  <MapPin className="h-3 w-3" />
                  EXPLORE ON MAP
                </button>
              </div>
            </div>

            {/* Location Search */}
            <div className="bg-white rounded-lg shadow-sm mb-4 p-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input placeholder="Search for locality / hotel" className="pl-8 h-8 text-xs" />
              </div>
            </div>

            {/* Business Essentials */}
            <div className="bg-white rounded-lg shadow-sm mb-4 p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Business Essentials</h3>
              <div className="space-y-2.5">
                {FILTER_OPTIONS.essentials.map((filter) => (
                  <label key={filter.id} className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={selectedFilters.has(filter.id)} onCheckedChange={() => toggleFilter(filter.id)} className="h-4 w-4" />
                      <span className="text-xs text-gray-700 group-hover:text-blue-600">{filter.label}</span>
                    </div>
                    <span className="text-[10px] text-gray-400">(5)</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Suggested For You */}
            <div className="bg-white rounded-lg shadow-sm mb-4 p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Suggested For You</h3>
              <div className="space-y-2.5">
                {FILTER_OPTIONS.suggestions.map((filter) => (
                  <label key={filter.id} className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={selectedFilters.has(filter.id)} onCheckedChange={() => toggleFilter(filter.id)} className="h-4 w-4" />
                      <span className="text-xs text-gray-700 group-hover:text-blue-600">{filter.label}</span>
                    </div>
                    <span className="text-[10px] text-gray-400">(9)</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Price per Room/Night</h3>
              <Slider value={priceRange} onValueChange={setPriceRange} max={50000} step={500} className="mb-3" />
              <div className="flex justify-between text-xs text-gray-500">
                <span>₹{priceRange[0].toLocaleString('en-IN')}</span>
                <span>₹{priceRange[1].toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Results Header + Sort */}
            <div className="mb-4">
              <h1 className="text-xl font-bold text-gray-900 mb-3">
                {total} Properties {filters.location && `in ${filters.location}`}
              </h1>
              
              <div className="flex items-center bg-white rounded-lg shadow-sm overflow-hidden">
                <button className="p-2 text-gray-400 hover:text-gray-600">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setFilters({ ...filters, sortBy: option.id })}
                    className={`px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                      filters.sortBy === option.id
                        ? 'text-blue-600 border-blue-600 bg-blue-50/50'
                        : 'text-gray-600 border-transparent hover:text-blue-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
                <button className="p-2 text-gray-400 hover:text-gray-600 ml-auto">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Property Listings */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              </div>
            ) : properties.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center shadow-sm">
                <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Properties Found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your search filters</p>
              </div>
            ) : (
              <div className="space-y-4">
                {properties.map((property) => {
                  const rating = getPropertyRating(property);
                  const pricing = portalPrices[property.id];
                  const basePrice = property.min_price || property.starting_price || 0;
                  const hasDiscount = pricing && pricing.discount_percent > 0;
                  const displayPrice = hasDiscount ? pricing.portal_price : basePrice;
                  const savings = hasDiscount ? Math.round(basePrice - pricing.portal_price) : 0;
                  const roomTypes = property.room_types || [];
                  
                  return (
                    <div
                      key={property.id}
                      className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow group"
                      data-testid={`property-card-${property.id}`}
                    >
                      {/* Property Header - Clickable */}
                      <div className="flex cursor-pointer" onClick={() => handlePropertyClick(property)}>
                        {/* Image */}
                        <div className="w-[260px] flex-shrink-0 relative">
                          <div className="h-[200px] relative overflow-hidden">
                            {property.photos?.[0] ? (
                              <img src={property.photos[0]} alt={property.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                <Building2 className="h-14 w-14 text-gray-400" />
                              </div>
                            )}
                          </div>
                          {property.photos?.length > 1 && (
                            <div className="absolute bottom-2 left-2 right-2 flex gap-1">
                              {property.photos.slice(1, 4).map((photo, idx) => (
                                <div key={idx} className="w-[50px] h-[35px] rounded overflow-hidden border border-white/80">
                                  <img src={photo} alt="" className="w-full h-full object-cover" />
                                </div>
                              ))}
                              {property.photos.length > 4 && (
                                <div className="w-[50px] h-[35px] rounded bg-black/70 flex items-center justify-center text-white text-[10px] font-medium">View All</div>
                              )}
                            </div>
                          )}
                          <div className="absolute top-2 left-2">
                            <div className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm">
                              <Check className="h-3 w-3" /> ASSURED
                            </div>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="flex-1 p-4 flex">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={`${rating.color} text-white text-xs font-bold px-1.5 py-0.5 rounded`}>{rating.score.toFixed(1)}</span>
                              <span className="text-xs font-semibold text-gray-700">{rating.label}</span>
                              <span className="text-[10px] text-gray-400">({property.review_count || Math.floor(Math.random() * 500) + 100} Ratings)</span>
                            </div>
                            
                            <h3 className="text-base font-bold text-gray-900 mb-0.5 group-hover:text-blue-600 transition-colors">
                              {property.name}
                              <span className="ml-2 inline-flex gap-0.5">
                                {[...Array(Math.min(property.star_rating || 4, 5))].map((_, i) => (
                                  <Star key={i} className="h-3 w-3 text-orange-400 fill-orange-400 inline" />
                                ))}
                              </span>
                            </h3>
                            
                            <div className="text-xs text-blue-600 font-medium mb-2">
                              {property.city || property.address?.city}
                            </div>
                            
                            <div className="space-y-1 mb-2">
                              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                <Check className="h-3 w-3 text-green-600" />
                                Free Cancellation till 24 hrs before check in
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                <span className="h-1.5 w-1.5 bg-gray-400 rounded-full" />
                                Breakfast available at extra charges
                              </div>
                            </div>
                            
                            <div className="flex items-start gap-1.5 text-xs text-gray-500">
                              <Sparkles className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">
                                {property.description || 'Modern amenities, excellent service, and prime location.'}
                              </span>
                            </div>
                          </div>

                          {/* Starting Price Section */}
                          <div className="w-[160px] flex-shrink-0 text-right pl-4 border-l border-gray-100">
                            {savings > 0 && (
                              <div className="text-xs text-orange-600 font-medium mb-1">Savings ₹ {savings.toLocaleString('en-IN')}</div>
                            )}
                            <div className="text-[10px] text-gray-500 mb-0.5">Starting from</div>
                            {hasDiscount && (
                              <div className="text-xs text-gray-400 line-through">₹ {basePrice?.toLocaleString('en-IN')}</div>
                            )}
                            <div className="text-2xl font-extrabold text-gray-900">₹ {displayPrice ? displayPrice.toLocaleString('en-IN') : 'N/A'}</div>
                            {displayPrice > 0 && (
                              <div className="text-[10px] text-gray-400">per room/night</div>
                            )}
                            {loadingPrices && !pricing && (
                              <div className="flex items-center justify-end gap-1 text-[10px] text-orange-500 mt-1">
                                <Loader2 className="h-3 w-3 animate-spin" />Finding best price...
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Room Types Section - Show available rooms with Quick Book */}
                      {roomTypes.length > 0 && (
                        <div className="border-t border-gray-100">
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                            <span className="text-xs font-bold text-gray-700">{roomTypes.length} Room Type{roomTypes.length > 1 ? 's' : ''} Available</span>
                            <div className="flex items-center gap-2">
                              {!hasDates && <span className="text-[10px] text-orange-500">Select dates for Quick Book</span>}
                              {portalSettings.show_competitor_prices && hasDates && (
                                <span className="text-[10px] text-green-600 flex items-center gap-1">
                                  <TrendingDown className="h-3 w-3" /> Competitor prices shown
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {roomTypes.slice(0, 3).map((room) => {
                              const roomPricing = roomPrices[room.id];
                              const isLoadingRoom = loadingRoomPrices[room.id];
                              const hasRoomDiscount = roomPricing && roomPricing.discount_percent > 0;
                              const displayRoomPrice = hasRoomDiscount ? roomPricing.portal_price : room.base_price;
                              const roomSavings = hasRoomDiscount ? Math.round(room.base_price - roomPricing.portal_price) : 0;
                              const competitors = roomPricing?.competitors || [];
                              const isExpanded = expandedCompetitors[room.id];
                              const showCompetitors = portalSettings.show_competitor_prices && competitors.length > 0;
                              
                              return (
                                <div key={room.id} data-testid={`room-type-${room.id}`}>
                                  <div className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-sm text-gray-900">{room.name}</span>
                                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                          <Users className="h-3 w-3" /> Max {room.max_occupancy || 2}
                                        </span>
                                      </div>
                                      {room.amenities?.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                          {room.amenities.slice(0, 4).map((amenity, idx) => (
                                            <span key={idx} className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                              {amenity}
                                            </span>
                                          ))}
                                          {room.amenities.length > 4 && (
                                            <span className="text-[10px] text-blue-500">+{room.amenities.length - 4} more</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      {/* Competitor Price Toggle */}
                                      {showCompetitors && (
                                        <button 
                                          className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedCompetitors(prev => ({ ...prev, [room.id]: !prev[room.id] }));
                                          }}
                                          data-testid={`toggle-competitors-${room.id}`}
                                        >
                                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                          Compare Prices
                                        </button>
                                      )}
                                      <div className="text-right min-w-[100px]">
                                        {isLoadingRoom ? (
                                          <div className="flex items-center justify-end gap-1.5">
                                            <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                                            <span className="text-xs text-orange-500">Finding offer...</span>
                                          </div>
                                        ) : (
                                          <>
                                            {roomSavings > 0 && (
                                              <div className="text-[10px] text-orange-600 font-medium">Save ₹{roomSavings.toLocaleString('en-IN')}</div>
                                            )}
                                            {hasRoomDiscount && (
                                              <div className="text-xs text-gray-400 line-through">₹{(room.base_price || 0).toLocaleString('en-IN')}</div>
                                            )}
                                            <div className="text-lg font-bold text-gray-900">₹ {(displayRoomPrice || 0).toLocaleString('en-IN')}</div>
                                            <div className="text-[10px] text-gray-400">per night</div>
                                          </>
                                        )}
                                      </div>
                                      <Button 
                                        size="sm" 
                                        className="font-bold text-xs px-4 text-white"
                                        style={{ background: hasDates ? 'linear-gradient(93deg, #53b2fe, #065af3)' : '#9ca3af' }}
                                        disabled={!hasDates}
                                        onClick={(e) => { e.stopPropagation(); handleQuickBook(property, room); }}
                                        data-testid={`quick-book-${room.id}`}
                                      >
                                        QUICK BOOK »
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Competitor Prices Panel */}
                                  {showCompetitors && isExpanded && (
                                    <div className="px-4 pb-3 bg-gradient-to-r from-blue-50 to-green-50 border-t border-blue-100">
                                      <div className="flex items-center gap-2 py-2 mb-2 border-b border-blue-100/50">
                                        <TrendingDown className="h-4 w-4 text-green-600" />
                                        <span className="text-xs font-semibold text-gray-700">Price Comparison</span>
                                        <span className="text-[10px] text-gray-500">(per night)</span>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        {competitors.map((competitor, idx) => {
                                          const isCheaper = displayRoomPrice < competitor.price_per_night;
                                          const priceDiff = competitor.price_per_night - displayRoomPrice;
                                          return (
                                            <div 
                                              key={idx} 
                                              className={`p-2 rounded-lg border ${isCheaper ? 'bg-white border-gray-200' : 'bg-orange-50 border-orange-200'}`}
                                              data-testid={`competitor-${competitor.platform}`}
                                            >
                                              <div className="text-[10px] text-gray-500 truncate">{competitor.platform}</div>
                                              <div className={`text-sm font-bold ${isCheaper ? 'text-gray-600' : 'text-orange-600'}`}>
                                                ₹{competitor.price_per_night?.toLocaleString('en-IN')}
                                              </div>
                                              {isCheaper && priceDiff > 0 && (
                                                <div className="text-[9px] text-green-600 font-medium">
                                                  You save ₹{priceDiff.toLocaleString('en-IN')}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <div className="mt-2 pt-2 border-t border-blue-100/50 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <div className="bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                                            OUR PRICE
                                          </div>
                                          <span className="text-lg font-extrabold text-green-700">
                                            ₹{(displayRoomPrice || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                        {roomPricing?.lowest_competitor && displayRoomPrice < roomPricing.lowest_competitor && (
                                          <span className="text-[10px] text-green-600 font-medium bg-green-100 px-2 py-0.5 rounded-full">
                                            Lowest price guaranteed!
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {roomTypes.length > 3 && (
                              <div className="px-4 py-2 text-center">
                                <button 
                                  className="text-xs text-blue-600 font-medium hover:underline"
                                  onClick={() => handlePropertyClick(property)}
                                >
                                  View all {roomTypes.length} room types →
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {hasDiscount && (
                        <div className="bg-green-50 px-4 py-2 flex items-center gap-2 border-t border-green-100">
                          <span className="text-xs text-green-800 font-medium">Special Offer: {pricing.discount_percent}% discount applied - Best price guaranteed!</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {properties.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-xs">
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <span className="px-4 py-2 text-xs text-gray-600">Page {page} of {Math.ceil(total / 10) || 1}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={properties.length < 10} className="text-xs">
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPortalSearch;
